import { useRef, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { axiosClient } from '../../../library/Service/axiosClient';
import { ACTIVITY_CATEGORIES } from './activityCategories';

// ─── CSV helpers ──────────────────────────────────────────────────────────────

function unquote(s) {
  let t = s.trim();
  if (t.startsWith('"') && t.endsWith('"') && t.length >= 2) t = t.slice(1, -1);
  return t.replace(/""/g, '"');
}

function splitCSVLine(line) {
  const out = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
      else inQ = !inQ;
    } else if (ch === ',' && !inQ) {
      out.push(cur); cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out.map(unquote);
}

function parseCSV(text) {
  const lines = text.replace(/^﻿/, '').split(/\r\n|\n|\r/).filter(l => l.trim());
  if (lines.length < 2) return { headers: [], rows: [] };
  const rawHeaders = splitCSVLine(lines[0]);
  const headers = rawHeaders.map(h => h.toLowerCase().trim());
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = splitCSVLine(lines[i]);
    const row = {};
    headers.forEach((h, idx) => { row[h] = cells[idx] ?? ''; });
    rows.push({ _rowNumber: i + 1, ...row });
  }
  return { headers, rows };
}

// ─── Validators ───────────────────────────────────────────────────────────────

function isValidYYYYMMDD(val) {
  if (!val || String(val).trim() === '') return false;
  const s = String(val).trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [y, m, d] = s.split('-').map(Number);
  if (m < 1 || m > 12 || d < 1 || d > 31) return false;
  return !isNaN(new Date(y, m - 1, d).getTime());
}

function isNonNegInt(val) {
  if (val === null || val === undefined) return false;
  const s = String(val).trim();
  return /^\d+$/.test(s);
}

// Validate a single parsed row → returns array of human-readable errors (empty = valid)
function validateRow(row) {
  const errors = [];

  // 1. All fields mandatory
  for (const field of REQUIRED_HEADERS) {
    const v = row[field];
    if (v === null || v === undefined || String(v).trim() === '') {
      errors.push(`Missing mandatory field: ${field}`);
    }
  }
  if (errors.length) return errors;

  // 2. institute_id numeric
  if (!isNonNegInt(row.institute_id)) errors.push('institute_id must be a number');

  // 3. event_date format
  if (!isValidYYYYMMDD(row.event_date)) {
    errors.push(`event_date must be in YYYY-MM-DD format (got "${row.event_date}")`);
  }

  // 4. source_type ∈ {2, 3}
  if (!VALID_SOURCE_TYPES.has(String(row.source_type).trim())) {
    errors.push(`source_type must be 2 or 3 (got "${row.source_type}")`);
  }

  // 4b. category ∈ allowed set (same values as the Activity Log Report)
  if (!VALID_CATEGORIES.has(String(row.category).trim().toUpperCase())) {
    errors.push(`category must be one of the allowed values (got "${row.category}")`);
  }

  // 5. counts numeric
  const countFields = ['sent_count', 'delivered_count', 'open_count', 'click_count'];
  const numericOk = {};
  for (const f of countFields) {
    if (!isNonNegInt(row[f])) errors.push(`${f} must be a non-negative number`);
    else numericOk[f] = parseInt(String(row[f]).trim(), 10);
  }

  // 6. Relationship checks (only if the involved counts are numeric)
  if (numericOk.sent_count != null && numericOk.delivered_count != null && numericOk.sent_count < numericOk.delivered_count) {
    errors.push('sent_count must be ≥ delivered_count');
  }
  if (numericOk.delivered_count != null && numericOk.open_count != null && numericOk.open_count > numericOk.delivered_count) {
    errors.push('open_count must be ≤ delivered_count');
  }
  if (numericOk.delivered_count != null && numericOk.click_count != null && numericOk.click_count > numericOk.delivered_count) {
    errors.push('click_count must be ≤ delivered_count');
  }

  return errors;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const REQUIRED_HEADERS = [
  'institute_id', 'subject', 'sent_count', 'delivered_count',
  'open_count', 'click_count', 'event_date', 'source_type', 'category',
];
const VALID_SOURCE_TYPES = new Set(['2', '3']);
// MoEngage templates do not support WATCHLIST (custom-watchlist CSV only).
const MOENGAGE_CATEGORIES = ACTIVITY_CATEGORIES.filter((c) => c.code !== 'WATCHLIST');
const VALID_CATEGORIES = new Set(MOENGAGE_CATEGORIES.map(({ code }) => code));
// Unlike the CSV path, this endpoint rejects unknown categories rather than coercing them,
// so the codes advertised here must track VALID_CATEGORIES exactly.
const CATEGORY_CODES = MOENGAGE_CATEGORIES.map(({ code }) => code).join(', ');
const MAX_ROWS = 2000;
const MAX_SIZE = 10 * 1024 * 1024; // 10 MB

const SAMPLE_CSV =
  'institute_id,subject,sent_count,delivered_count,open_count,click_count,event_date,source_type,category\n' +
  '10010006603,Test for engagement,1000,500,200,200,2026-05-31,2,NOSTALGIA\n';

// ─── Component ───────────────────────────────────────────────────────────────

function AddMoengageModal({ className, onSuccess, isSuperAdmin, instituteDomain }) {
  const [show, setShow] = useState(false);
  const [file, setFile] = useState(null);
  const [parsedRows, setParsedRows] = useState([]);
  const [parseError, setParseError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const resetFile = () => {
    setFile(null);
    setParsedRows([]);
    setParseError('');
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleClose = () => {
    setShow(false);
    resetFile();
    setIsUploading(false);
    setResult(null);
  };

  const failFile = (msg) => {
    setParseError(msg);
    setFile(null);
    setParsedRows([]);
    if (inputRef.current) inputRef.current.value = '';
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'moengage_sample.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleFileChange = (e) => {
    setResult(null);
    setParseError('');
    setParsedRows([]);
    const f = e.target.files?.[0];
    if (!f) { setFile(null); return; }

    if (!f.name.toLowerCase().endsWith('.csv')) {
      failFile('Invalid file format. Please upload a .csv file.');
      return;
    }
    if (f.size > MAX_SIZE) {
      failFile('File too large. Max allowed size is 10 MB.');
      return;
    }

    setFile(f);

    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = String(ev.target?.result ?? '');
      const { headers, rows } = parseCSV(text);

      const missing = REQUIRED_HEADERS.filter(h => !headers.includes(h));
      if (missing.length) { failFile(`Missing required column(s): ${missing.join(', ')}`); return; }
      if (rows.length === 0) { failFile('No data rows found in the CSV.'); return; }
      if (rows.length > MAX_ROWS) { failFile(`File has ${rows.length} rows. Maximum allowed is ${MAX_ROWS}.`); return; }

      // Validate every row up front so the user fixes the file before sending.
      const invalid = [];
      for (const r of rows) {
        const errs = validateRow(r);
        if (errs.length) invalid.push({ rowNumber: r._rowNumber, errors: errs });
      }
      if (invalid.length) {
        const preview = invalid.slice(0, 5)
          .map(r => `Row ${r.rowNumber}: ${r.errors.join('; ')}`)
          .join(' | ');
        const extra = invalid.length > 5 ? ` …and ${invalid.length - 5} more invalid row(s).` : '';
        failFile(`${invalid.length} invalid row(s). ${preview}${extra}`);
        return;
      }

      setParsedRows(rows);
    };
    reader.readAsText(f);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file || parsedRows.length === 0) {
      setResult({ success: false, message: 'Please select a valid CSV file.' });
      return;
    }

    setIsUploading(true);
    setResult(null);

    const rowsToSend = parsedRows.map(({ _rowNumber, ...r }) => r);

    try {
      const res = await axiosClient().post(
        `${process.env.REACT_APP_BROADCAST_SERVER}/referral/add-moengage-template`,
        { rows: rowsToSend }
      );
      setResult(res.data);
      if (res.data?.insertedCount > 0) onSuccess?.();
    } catch (err) {
      setResult({
        success: false,
        message: err?.response?.data?.message || err?.message || 'Upload failed.',
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <Button className={className} onClick={() => setShow(true)}>
        <i className="icon-upload-big-arrow text-white me-2 position-relative"></i>
        <span>Add Moengage</span>
      </Button>

      <Modal className="loginModal" show={show} onHide={handleClose} size="lg" centered>
        <div className="modal-header p-4 height-70 border-0 bg-gray-f2">
          <h5 className="modal-title">Add MoEngage Entries (Bulk Upload)</h5>
          <Button variant="close" onClick={handleClose} />
        </div>

        <Modal.Body className="p-4">
          <form noValidate onSubmit={handleUpload}>
            <div className="row g-3">

              {/* File input */}
              <div className="col-12">
                <label className="name font-500 mb-2 d-flex align-items-center">
                  Upload File{' '}
                  <span className="ms-2 text-muted font-13 font-400">(.csv only, max 10 MB, max {MAX_ROWS} rows)</span>
                  <button
                    type="button"
                    onClick={downloadSample}
                    className="btn-darkblue height-32 px-3 font-400 text-decoration-none ms-2 border-0"
                  >
                    <span>Download Sample</span>
                  </button>
                </label>
                <div className="file file--upload">
                  <label htmlFor="moengage-csv-file" className="py-3">
                    <i className="material-icons img-fluid">
                      <img
                        alt="upload"
                        src="https://s3.ap-south-1.amazonaws.com/media.univariety.com/userreactapp/style-images/cloud-computing.svg"
                        width="30"
                      />
                    </i>
                    <div>Drag and drop here · CSV files only (Max 10 MB / Max 2,000 rows)</div>
                  </label>
                  <input
                    id="moengage-csv-file"
                    ref={inputRef}
                    type="file"
                    accept=".csv"
                    className="d-block position-absolute top-0 w-100 h-100 opacity-0"
                    onChange={handleFileChange}
                  />
                </div>
              </div>

              {/* Parse error */}
              {parseError && (
                <div className="col-12">
                  <div className="alert alert-danger p-2 mb-0">
                    <i className="bi bi-exclamation-triangle-fill me-2" />
                    {parseError}
                  </div>
                </div>
              )}

              {/* File ready preview */}
              {file && parsedRows.length > 0 && !parseError && (
                <div className="col-12">
                  <div className="alert alert-info p-2 mb-0">
                    <b>Selected:</b> {file.name} &nbsp;|&nbsp; <b>{parsedRows.length}</b> data row(s) ready to upload.
                  </div>
                </div>
              )}

              {/* Result summary */}
              {result && (
                <>
                  {result.message && (
                    <div className="col-12">
                      <div className={`alert ${result.success ? 'alert-success' : 'alert-danger'} p-2 mb-0`}>
                        {result.message}
                      </div>
                    </div>
                  )}

                  {Array.isArray(result.failedRows) && result.failedRows.length > 0 && (
                    <div className="col-12">
                      <div className="border rounded p-2" style={{ maxHeight: 260, overflowY: 'auto' }}>
                        <div className="font-13 font-500 mb-2 text-danger">
                          Failed Rows ({result.failedRows.length}):
                        </div>
                        {result.failedRows.slice(0, 100).map((r, idx) => (
                          <div key={idx} className="mb-2 font-13">
                            <b>Row {r.rowNumber}:</b>{' '}
                            {Array.isArray(r.errors) ? r.errors.join(' | ') : String(r.errors || '')}
                            {idx < result.failedRows.length - 1 && <hr className="my-1" />}
                          </div>
                        ))}
                        {result.failedRows.length > 100 && (
                          <div className="text-muted font-13">
                            …and {result.failedRows.length - 100} more failed rows.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Upload button — visible until result arrives */}
              {!result && (
                <div className="col-12 text-center">
                  <button
                    type="submit"
                    className="btn btn-green max-135 mx-auto d-flex align-items-center gap-2"
                    disabled={isUploading || !!parseError || parsedRows.length === 0}
                  >
                    {isUploading && (
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      />
                    )}
                    <span>{isUploading ? 'Uploading…' : 'Upload'}</span>
                  </button>
                </div>
              )}

              {/* Column reference */}
              <div className="col-12 text-muted" style={{ fontSize: 12 }}>
                Required columns: <code>institute_id, subject, sent_count, delivered_count, open_count, click_count, event_date, source_type, category</code>
                <br />
                <span className="text-danger fw-500">event_date :- yyyy-mm-dd</span> &nbsp;(e.g. <code>2026-05-31</code>) — other formats will be rejected.
                <br />
                <code>source_type</code>: <code>2</code> (Email) or <code>3</code> (WhatsApp) only.
                <br />
                Category values: <code>{CATEGORY_CODES}</code>
                <br />
                All fields are mandatory · sent_count ≥ delivered_count · open_count / click_count ≤ delivered_count.
              </div>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default AddMoengageModal;
